import { EnrollmentView } from "components/enrollments/EnrollmentView";
import { EnrollmentSubmission } from "models/enrollments/EnrollmentSubmission";
import { useState, useMemo } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import type { UserSubmissionDto } from 'models/projects/UserSubmissionDto';
import UserSubmission from "./UserSubmission";

type Props = {
    enrollmentSubmissions: Array<EnrollmentSubmission>;
    onSubmit?: (submissionDto: UserSubmissionDto) => void;
    className?: string;
    projectId: string;
    workflowId: string;
    isOwner: boolean;
    isTeamMember: boolean;
};

const ProgressView: React.FC<Props> = ({ enrollmentSubmissions, onSubmit, className, projectId, workflowId, isOwner, isTeamMember }) => {
    // initialize to the last item if available, otherwise 0
    const maxIndex = Math.max(0, enrollmentSubmissions.length - 1);
    const [index, setIndex] = useState<number>(maxIndex);
    const [enrollmentSubmission, setEnrollmentSubmission] = useState<EnrollmentSubmission | null>(enrollmentSubmissions[index]);

    const handlePrev = () => {
        setIndex((prev) => {
            const nextIdx = Math.max(0, prev - 1);
            setEnrollmentSubmission(enrollmentSubmissions[nextIdx]);
            return nextIdx;
        });
    };

    const handleNext = () => {
        setIndex((prev) => {
            const nextIdx = Math.min(enrollmentSubmissions.length - 1, prev + 1);
            setEnrollmentSubmission(enrollmentSubmissions[nextIdx]);
            return nextIdx;
        });
    };

    const percent = useMemo(() => ((index + 1) / enrollmentSubmissions.length) * 100, [index, enrollmentSubmissions.length]);

    if (enrollmentSubmissions.length === 0) {
        return (
            <div className="text-center text-gray-600">No enrollment submissions available.</div>
        );
    }

    const isAtStart = index <= 0;
    const isAtEnd = index >= enrollmentSubmissions.length - 1;

    return (
        <div className={"w-full max-w-3xl " + (className || "")}>
            <div className="flex items-center justify-center gap-4">
                <button
                    onClick={handlePrev}
                    disabled={isAtStart}
                    aria-label="Previous submission"
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isAtStart ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                >
                    <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>

                <div className="flex-1">
                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        {enrollmentSubmission && enrollmentSubmission.userSubmission ? (
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">User Submission</h3>
                                <UserSubmission submission={enrollmentSubmission.userSubmission} isOwner={isOwner} isTeamMember={isTeamMember} projectId={projectId} />
                            </div>
                        ) : (
                            <EnrollmentView
                                enrollment={enrollmentSubmission?.enrollment}
                                onSubmit={(submissionDto) => { onSubmit?.(submissionDto); }}
                                workflowId={workflowId}
                                nodeId={enrollmentSubmission?.nodeId}
                                projectId={projectId}
                                isDisabled={false}
                            />
                        )}
                    </div>
                    <div className="mt-3 text-center text-sm text-gray-600">
                        Stage {index + 1} of {enrollmentSubmissions.length}
                        <div className="mt-2 h-2 w-full bg-gray-200 rounded-full" aria-label="Progress bar">
                            <div
                                className="h-2 bg-blue-500 rounded-full transition-all"
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleNext}
                    disabled={isAtEnd}
                    aria-label="Next submission"
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isAtEnd ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                >
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

export default ProgressView;